import { useTranslation } from 'next-i18next';
import { Box, HStack, styled } from 'styled-system/jsx';
import { Text } from '@ui/lib/park/ui/text';
import { formatBigValue } from '@ui/lib/bignumber';
import { useContext, useMemo } from 'react';
import { ExchangeContext } from '../Context';
import { parseUnits } from 'viem';

type DailyRedeemLimitProps = {
  totalLimit: bigint;
  remaining: bigint;
  nextResetTime: bigint;
  isExceeded: boolean;
  status: readonly [bigint, bigint, bigint] | undefined;
};

export const DailyRedeemLimit = ({ totalLimit, remaining, nextResetTime, isExceeded, status }: DailyRedeemLimitProps) => {
  const { t } = useTranslation();
  const { inputAmount, inputToken } = useContext(ExchangeContext);

  const totalFormatted = formatBigValue(totalLimit, {
    decimals: 18,
    displayDecimals: 2,
  });

  const remainingFormatted = formatBigValue(remaining, {
    decimals: 18,
    displayDecimals: 2,
  });

  const inputAmountBN = useMemo(() => {
    if (!inputAmount || !inputToken) return 0n;
    try {
      return parseUnits(inputAmount, inputToken.decimals);
    } catch {
      return 0n;
    }
  }, [inputAmount, inputToken]);

  const remainingAfterInputFormatted = useMemo(() => {
    const rem = remaining - inputAmountBN;
    if (rem < 0n) return '0.00';
    return formatBigValue(rem, {
      decimals: 18,
      displayDecimals: 2,
    });
  }, [remaining, inputAmountBN]);

  const displayedRemaining = inputAmountBN > 0n ? remainingAfterInputFormatted : remainingFormatted;

  const now = Math.floor(Date.now() / 1000);
  const timeUntilReset = Number(nextResetTime) - now;
  
  const hours = Math.max(0, Math.floor(timeUntilReset / 3600));
  const minutes = Math.max(0, Math.floor((timeUntilReset % 3600) / 60));
  
  const timeString = timeUntilReset > 0 
    ? `${hours}h ${minutes}m`
    : '0h 0m';

  return (
    <Box w="full" py={2}>
      <HStack alignItems="center" justifyContent="space-between" gap={2}>
        <Text color="rgba(255,255,255,0.5)" fontSize={12} fontWeight={400}>
          Daily Remaining:
        </Text>
        <HStack alignItems="center" gap={1} whiteSpace="nowrap">
          <styled.img src="/tokens/LVMON.svg" width="16px" height="16px" />
          <Text color={isExceeded ? 'red' : 'greyWhite.lightGrey1'} fontSize={13} fontWeight={500}>
            {displayedRemaining}/{totalFormatted}
          </Text>
          <Text color="rgba(255,255,255,0.5)" fontSize={12} fontWeight={400}>
            (Resets in {timeString})
          </Text>
        </HStack>
      </HStack>
    </Box>
  );
};
